import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { UserPlus, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const { user, register } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name.trim(), email.trim().toLowerCase(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div
        className="hidden lg:flex relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, rgba(212, 255, 58, 0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(212, 255, 58, 0.12), transparent 50%)",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img src="/adxsk-logo-dark.png" alt="Akshat Dani x SK" style={{ height: 44, width: "auto" }} />
          </div>
          <div className="space-y-6 max-w-lg">
            <p className="label-uppercase" style={{ color: "var(--lime)" }}>// Get Started</p>
            <h1 className="font-display text-5xl font-black leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
              Create your account.<br />
              Start consulting in <span style={{ color: "var(--lime)" }}>minutes</span>.
            </h1>
            <p className="text-lg opacity-70 leading-relaxed">
              Your conversations, history, and preferences live with your account — synced and private.
            </p>
          </div>
          <div className="opacity-50 text-xs font-mono uppercase tracking-widest">© Akshat Dani</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-8">
            <button className="btn btn-ghost btn-sm" onClick={toggle}>
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
          </div>

          <p className="label-uppercase mb-3" style={{ color: "var(--accent)" }}>New Account</p>
          <h2 className="font-display text-4xl font-bold mb-2">Create your hub</h2>
          <p className="mb-8" style={{ color: "var(--muted)" }}>
            Just a few details and you're in.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="label-uppercase block mb-2" htmlFor="name">Name</label>
              <input
                id="name"
                type="text"
                className="input"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="label-uppercase block mb-2" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="label-uppercase block mb-2" htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="input"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="register-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="register-error"
                className="text-sm py-2 px-3 rounded-md"
                style={{ background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.3)" }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg w-full"
              disabled={loading}
              data-testid="register-submit-button"
            >
              <UserPlus size={18} weight="duotone" />
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-sm text-center" style={{ color: "var(--muted)" }}>
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login-link" className="font-semibold">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
